import { MapPin, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/ui';

/**
 * Structured address returned by Google Places autocomplete.
 * Falls back to manual entry if Google Places API is not loaded.
 */
export interface StructuredAddress {
  addressLine1: string;
  addressLine2?: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  formattedAddress?: string;
}

interface Props {
  value?: Partial<StructuredAddress>;
  onChange: (address: StructuredAddress) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Minimal type declarations for Google Maps Places API (optional dependency)
// eslint-disable-next-line @typescript-eslint/no-namespace -- type-only declarations
declare namespace google.maps.places {
  class AutocompleteService {
    getPlacePredictions(request: any, callback: (results: any, status: any) => void): void;
  }
  class PlacesService {
    constructor(el: HTMLElement);
    getDetails(request: any, callback: (place: any, status: any) => void): void;
  }
  const PlacesServiceStatus: { OK: string };
  interface AutocompletePrediction {
    place_id: string;
    description: string;
    structured_formatting: { main_text: string; secondary_text: string };
  }
}

// Check if Google Places API is available
function isGoogleLoaded(): boolean {
  return (
    typeof google !== 'undefined' &&
    typeof google.maps !== 'undefined' &&
    typeof google.maps.places !== 'undefined'
  );
}

/**
 * Address autocomplete component.
 * Uses Google Places Autocomplete API when available (requires VITE_GOOGLE_PLACES_API_KEY).
 * Falls back to manual structured address entry if the API key is not configured.
 */
export function AddressAutocomplete({ value, onChange, placeholder, disabled }: Props) {
  const [query, setQuery] = useState(value?.formattedAddress || value?.addressLine1 || '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useManual, setUseManual] = useState(!isGoogleLoaded());
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const dummyDivRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Manual entry state
  const [manual, setManual] = useState<Partial<StructuredAddress>>({
    addressLine1: value?.addressLine1 || '',
    addressLine2: value?.addressLine2 || '',
    suburb: value?.suburb || '',
    state: value?.state || '',
    postcode: value?.postcode || '',
    country: value?.country || 'Australia',
  });

  // Initialize Google services
  useEffect(() => {
    if (isGoogleLoaded()) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      if (dummyDivRef.current) {
        placesServiceRef.current = new google.maps.places.PlacesService(dummyDivRef.current);
      }
      setUseManual(false);
    } else {
      setUseManual(true);
    }
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch autocomplete predictions
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteServiceRef.current || input.length < 3) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'au' },
        types: ['address'],
      },
      (results: any, status: any) => {
        setLoading(false);
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
        }
      },
    );
  }, []);

  // Debounced input handler
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  function handleInputChange(val: string) {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(val), 300);
  }

  // Select a prediction and resolve to structured address
  function handleSelect(prediction: google.maps.places.AutocompletePrediction) {
    if (!placesServiceRef.current) return;

    setLoading(true);
    setShowDropdown(false);
    setQuery(prediction.description);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'geometry', 'formatted_address', 'place_id'],
      },
      (place: any, status: any) => {
        setLoading(false);
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) return;

        const components: any[] = place.address_components ?? [];
        const get = (type: string) =>
          components.find((c: any) => c.types.includes(type))?.long_name ?? '';
        const getShort = (type: string) =>
          components.find((c: any) => c.types.includes(type))?.short_name ?? '';

        const streetNumber = get('street_number');
        const route = get('route');

        const address: StructuredAddress = {
          addressLine1: streetNumber ? `${streetNumber} ${route}` : route,
          suburb:
            get('locality') || get('sublocality_level_1') || get('administrative_area_level_2'),
          state: getShort('administrative_area_level_1'),
          postcode: get('postal_code'),
          country: get('country') || 'Australia',
          latitude: place.geometry?.location?.lat(),
          longitude: place.geometry?.location?.lng(),
          placeId: place.place_id,
          formattedAddress: place.formatted_address,
        };

        setQuery(place.formatted_address ?? prediction.description);
        onChange(address);
      },
    );
  }

  // Manual entry handler
  function handleManualChange(field: keyof StructuredAddress, val: string) {
    const updated = { ...manual, [field]: val };
    setManual(updated);
    onChange({
      addressLine1: updated.addressLine1 || '',
      addressLine2: updated.addressLine2 || '',
      suburb: updated.suburb || '',
      state: updated.state || '',
      postcode: updated.postcode || '',
      country: updated.country || 'Australia',
    });
  }

  // Manual entry mode
  if (useManual) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Property Address
          </label>
          {isGoogleLoaded() && (
            <button
              type="button"
              onClick={() => setUseManual(false)}
              className="text-xs text-indigo-600 hover:text-indigo-700"
            >
              Use autocomplete
            </button>
          )}
        </div>
        <input
          value={manual.addressLine1 || ''}
          onChange={e => handleManualChange('addressLine1', e.target.value)}
          placeholder="Street address"
          disabled={disabled}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        />
        <input
          value={manual.addressLine2 || ''}
          onChange={e => handleManualChange('addressLine2', e.target.value)}
          placeholder="Unit / Level (optional)"
          disabled={disabled}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        />
        <div className="grid grid-cols-3 gap-3">
          <input
            value={manual.suburb || ''}
            onChange={e => handleManualChange('suburb', e.target.value)}
            placeholder="Suburb"
            disabled={disabled}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
          <select
            value={manual.state || ''}
            onChange={e => handleManualChange('state', e.target.value)}
            disabled={disabled}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          >
            <option value="">State</option>
            {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <input
            value={manual.postcode || ''}
            onChange={e => handleManualChange('postcode', e.target.value)}
            placeholder="Postcode"
            maxLength={4}
            disabled={disabled}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>
      </div>
    );
  }

  // Google Places autocomplete mode
  return (
    <div className="relative">
      <div ref={dummyDivRef} style={{ display: 'none' }} />
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Property Address
        </label>
        <button
          type="button"
          onClick={() => setUseManual(true)}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          Enter manually
        </button>
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder ?? 'Start typing an address…'}
          disabled={disabled}
          className="w-full rounded-md border border-gray-300 pl-3 pr-10 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        />
        {loading && (
          <Spinner
            size="lg"
            className="absolute right-3 top-2.5 w-4 h-4 text-gray-400 animate-spin"
          />
        )}
        {!loading && query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setPredictions([]);
              onChange({
                addressLine1: '',
                suburb: '',
                state: '',
                postcode: '',
                country: 'Australia',
              });
            }}
            className="absolute right-3 top-2.5 text-gray-300 hover:text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Predictions dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-20 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg max-h-60 overflow-y-auto"
        >
          {predictions.map(p => (
            <button
              key={p.place_id}
              type="button"
              onClick={() => handleSelect(p)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
            >
              <p className="text-sm text-gray-900">{p.structured_formatting.main_text}</p>
              <p className="text-xs text-gray-400">{p.structured_formatting.secondary_text}</p>
            </button>
          ))}
          <div className="px-3 py-1.5 text-[10px] text-gray-300 text-right">Powered by Google</div>
        </div>
      )}
    </div>
  );
}
